<template>
  <div class="container-fluid py-4">
    <!-- Top Dashboard Summary Cards Bar -->
    <div class="row mb-4">
      <!-- STATS CARD 1: Total Playlists -->
      <div class="col-xl-3 col-sm-6 col-12 mb-4">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body p-3">
            <div class="row align-items-center">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-xs mb-0 text-uppercase font-weight-bold text-secondary">Total Playlists</p>
                  <h5 class="font-weight-bolder text-primary mb-0">{{ savedPlaylists.length }}</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                  <i class="fas fa-list-ul text-lg opacity-10 text-white" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STATS CARD 2: Gallery Media -->
      <div class="col-xl-3 col-sm-6 col-12 mb-4">
        <div class="card shadow-sm border-0 h-100 cursor-pointer" @click="openGalleryModal">
          <div class="card-body p-3">
            <div class="row align-items-center">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-xs mb-0 text-uppercase font-weight-bold text-info">Gallery Media</p>
                  <h5 class="font-weight-bolder text-info mb-0">{{ singleVideos.length }} Videos</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-info shadow-info text-center rounded-circle">
                  <i class="fas fa-photo-video text-lg opacity-10 text-white" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STATS CARD 3: BROADCAST LIVE / SELECTED PREVIEW -->
      <div class="col-xl-3 col-sm-6 col-12 mb-4">
        <div class="card shadow-sm border-0 h-100" :class="{'bg-gradient-success': isGlobalLive, 'bg-gradient-dark': !isGlobalLive}">
          <div class="card-body p-3">
            <div class="row align-items-center">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-xs mb-0 text-uppercase font-weight-bold text-white-50">
                    {{ isGlobalLive ? 'Broadcast Live' : 'Quick View' }}
                  </p>
                  <button 
                    class="btn btn-sm mt-2 mb-0" 
                    :class="isGlobalLive ? 'btn-white text-dark' : 'bg-gradient-success text-white'" 
                    @click="showSelectedModal = true"
                  >
                    <i class="fas fa-play me-1"></i> Selected ({{ selectedVideosCount }})
                  </button>
                </div>
              </div>
              <div class="col-4 text-end">
                <div 
                  class="icon icon-shape shadow-success text-center rounded-circle cursor-pointer"
                  :class="isGlobalLive ? 'bg-white text-success pulse-red' : 'bg-gradient-success text-white'"
                  @click="triggerGlobalEmergency"
                  style="cursor: pointer;"
                  title="Trigger Broadcast Now for Selected Items"
                >
                  <i class="fas fa-play text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STATS CARD 4: STOP EMERGENCY CONTROL CARD -->
      <div class="col-xl-3 col-sm-6 col-12 mb-4">
        <div class="card shadow-sm border-0 h-100" @click="stopAllBroadcast" style="cursor: pointer;">
          <div class="card-body p-3">
            <div class="row align-items-center">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-xs mb-0 text-uppercase font-weight-bold text-danger">Emergency Control</p>
                  <h5 class="font-weight-bolder text-danger mb-0">STOP ALL & REMOVE</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-danger shadow-danger text-center rounded-circle">
                  <i class="fas fa-stop text-lg opacity-10 text-white" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Live Videos Management Table Card -->
    <div class="row">
      <div class="col-12">
        <div class="card my-4 shadow-sm border-0">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 px-3 d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3 mb-0">Playlists Management</h6>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-light mb-0" @click="openModal(false)">
                  <i class="fas fa-plus me-1"></i> Add Live Video / Links
                </button>
              </div>
            </div>
          </div>

          <div class="card-body px-0 pb-2">
            <div class="row px-4 py-2 align-items-center justify-content-between">
              <div class="col-md-4">
                <div class="input-group input-group-outline">
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Search playlist or branch..."
                    v-model="searchQuery"
                  />
                </div>
              </div>

              <!-- Action buttons including AUDIT / TRASH BIN in red box section -->
              <div class="col-auto d-flex align-items-center gap-2">
                <button
                  class="btn btn-outline-danger mb-0 shadow-sm font-weight-bold text-nowrap audit-trash-btn"
                  @click="openTrashModal"
                >
                  <i class="fas fa-undo-alt me-1"></i> AUDIT / TRASH BIN ({{ trashedPlaylists.length }})
                </button>

                <button
                  class="btn btn-info mb-0 shadow-sm font-weight-bold text-nowrap"
                  @click="openGalleryModal"
                >
                  <i class="fas fa-images me-1"></i> GALLERY ({{ singleVideos.length }})
                </button>

                <button
                  class="btn btn-primary mb-0 shadow-sm font-weight-bold text-nowrap"
                  @click="showSelectedModal = true"
                  :disabled="savedPlaylists.length === 0 && singleVideos.length === 0"
                >
                  <i class="fas fa-play me-1"></i> SHOW PLAYLIST
                </button>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <div v-else class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Select</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Playlist Name</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Branch(es)</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Included Videos</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Active From</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Active Till</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Audit & Update History</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="filteredTablePlaylists.length === 0">
                    <td colspan="9" class="text-center py-4 text-sm text-secondary">
                      No active playlists found. Open <strong>GALLERY</strong> to select videos and create playlists or check <strong>AUDIT / TRASH BIN</strong>.
                    </td>
                  </tr>
                  <tr 
                    v-for="playlist in filteredTablePlaylists" 
                    :key="playlist.id + '_' + playlist.is_live" 
                    :class="{'bg-light-danger': isVideoLive(playlist)}"
                  >
                    <td class="text-center align-middle">
                      <div class="form-check d-flex justify-content-center p-0">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          :value="playlist.id"
                          v-model="selectedVideoIds"
                          style="cursor: pointer; width: 18px; height: 18px;"
                        />
                      </div>
                    </td>

                    <td>
                      <h6 class="mb-0 text-sm font-weight-bold text-dark">
                        {{ playlist.playlist_name || playlist.title }}
                        <span v-if="isVideoLive(playlist)" class="badge bg-danger ms-2 pulse-red-small">LIVE</span>
                      </h6>
                    </td>
                    <td>
                      <span class="badge bg-outline-secondary text-dark text-xs font-weight-bold">
                        <i class="ni ni-building text-xs me-1"></i>
                        {{ playlist.branch_name || 'All Branches' }}
                      </span>
                    </td>
                    <td>
                      <span class="badge bg-gradient-info text-xs">
                        <i class="fas fa-video me-1"></i>
                        {{ getPlaylistVideoIds(playlist.playlist_videos || playlist.video_file).length }} Videos
                      </span>
                    </td>
                    <td><span class="text-xs text-secondary font-weight-bold">{{ playlist.active_from ? formatDate(playlist.active_from) : 'Immediate' }}</span></td>
                    <td><span class="text-xs text-secondary font-weight-bold">{{ playlist.active_till ? formatDate(playlist.active_till) : 'Indefinite' }}</span></td>
                    <td class="align-middle text-center text-sm">
                      <span class="badge badge-sm" :class="isVideoActive(playlist) ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                        {{ isVideoActive(playlist) ? 'Active' : 'Inactive' }}
                      </span>
                    </td>

                    <!-- AUDIT & UPDATE HISTORY COLUMN -->
                    <td>
                      <div class="text-xxs text-dark">
                        <div><strong class="text-secondary">Created:</strong> {{ playlist.creator?.name || 'System' }}</div>
                        <div class="text-muted mb-1">{{ formatDate(playlist.created_at) }}</div>

                        <div v-if="playlist.updater" class="mt-1 pt-1 border-top">
                          <strong class="text-info">Updated:</strong> {{ playlist.updater?.name }}
                          <div class="text-muted">{{ formatDate(playlist.updated_at) }}</div>
                        </div>

                        <!-- BUTTON TO OPEN COMPLETE RECORD UPDATE HISTORY -->
                        <button class="btn btn-xs btn-outline-info mt-2 mb-0 w-100" @click="openHistoryModal(playlist)">
                          <i class="fas fa-history me-1"></i> History ({{ ensureArray(playlist.update_history).slice(0, 5).length }})
                        </button>
                      </div>
                    </td>

                    <td class="align-middle text-center">
                      <div class="d-flex justify-content-center align-items-center gap-1">
                        <button 
                          v-if="isVideoLive(playlist)" 
                          @click="stopSingleEmergency(playlist)" 
                          class="btn btn-sm btn-icon-only bg-gradient-danger rounded-circle me-1" 
                          title="Stop Live Broadcast (Move to Trash Bin)"
                        >
                          <i class="fas fa-stop"></i>
                        </button>
                        <button 
                          v-else 
                          @click="triggerPlaylistBroadcast(playlist)" 
                          class="btn btn-sm btn-icon-only bg-gradient-warning rounded-circle me-1" 
                          title="Broadcast Playlist Now"
                        >
                          <i class="fas fa-bolt"></i>
                        </button>
                        <button class="btn btn-link text-dark px-2 mb-0" @click="editPlaylist(playlist)">
                          <i class="fas fa-pencil-alt text-dark me-1"></i> Edit
                        </button>
                        <button class="btn btn-link text-danger text-gradient px-2 mb-0" @click="deleteVideo(playlist.id)">
                          <i class="far fa-trash-alt me-1"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL: UPDATE HISTORY TIMELINE (WHO UPDATED WHAT & WHEN) -->
    <div v-if="showRecordHistoryModal" class="modal-overlay p-2 p-md-0">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg w-100">
          <div class="modal-header border-0 bg-light pb-2">
            <h5 class="modal-title font-weight-bold text-dark">
              <i class="fas fa-history text-info me-2"></i> Record Update History: "{{ activeHistoryRecord?.playlist_name || activeHistoryRecord?.title }}"
            </h5>
            <button type="button" class="btn-close text-dark" @click="showRecordHistoryModal = false"></button>
          </div>
          <div class="modal-body" style="max-height: 70vh; overflow-y: auto;">
            <div v-if="activeHistoryLogs.length === 0" class="text-center py-4 text-muted">
              <i class="fas fa-info-circle fa-2x mb-2 d-block"></i>
              No detailed modification history recorded yet for this item.
            </div>

            <div v-else class="timeline timeline-one-side">
              <div v-for="(log, idx) in activeHistoryLogs" :key="idx" class="timeline-block mb-3 border-bottom pb-3">
                <span class="timeline-step">
                  <i class="fas" :class="getActionIcon(log.action)"></i>
                </span>
                <div class="timeline-content">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="text-dark text-sm font-weight-bold mb-0">
                      {{ log.action }} by <span class="text-primary">{{ log.user_name || 'System' }}</span>
                    </h6>
                    <small class="text-muted text-xxs"><i class="fas fa-clock me-1"></i>{{ formatDate(log.updated_at) }}</small>
                  </div>

                  <!-- FIELD CHANGES BREAKDOWN TABLE -->
                  <div v-if="log.changes && Object.keys(log.changes).length > 0" class="mt-2 bg-light p-2 rounded">
                    <table class="table table-sm text-xxs mb-0">
                      <thead>
                        <tr>
                          <th>Field Changed</th>
                          <th>Old Value</th>
                          <th>New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(val, field) in log.changes" :key="field">
                          <td class="font-weight-bold text-dark">{{ field }}</td>
                          <td class="text-danger text-truncate" style="max-width: 150px;">{{ formatValue(val.old) }}</td>
                          <td class="text-success text-truncate" style="max-width: 150px;">{{ formatValue(val.new) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0">
            <button class="btn btn-secondary w-100" @click="showRecordHistoryModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL: AUDIT / TRASH BIN -->
    <div v-if="showTrashModal" class="modal-overlay p-2 p-md-0">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg w-100">
          <div class="modal-header bg-gradient-dark text-white py-3 px-4 d-flex justify-content-between align-items-center">
            <h5 class="modal-title text-white font-weight-bold mb-0">
              <i class="fas fa-undo-alt me-2 text-danger"></i> Audit / Trash Bin (Stopped & Removed Records)
            </h5>
            <button type="button" class="btn-close text-white" @click="showTrashModal = false"></button>
          </div>

          <div class="modal-body p-4 bg-light" style="max-height: 70vh; overflow-y: auto;">
            <div v-if="trashedPlaylists.length === 0" class="text-center py-5 text-secondary">
              <i class="fas fa-trash-restore fa-3x mb-3 text-secondary"></i>
              <p class="font-weight-bold mb-0">Trash Bin is empty. No stopped or archived records found.</p>
            </div>

            <div v-else class="table-responsive bg-white rounded shadow-sm p-2">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">Record / Playlist Name</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Created By</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Updated By</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Stopped By</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Deleted By</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in trashedPlaylists" :key="item.id">
                    <td class="ps-3">
                      <h6 class="mb-0 text-sm font-weight-bold text-dark">{{ item.playlist_name || item.title }}</h6>
                      <span class="badge bg-gradient-info text-xs">{{ item.video_type === 'playlist' ? 'Playlist' : 'Media Item' }}</span>
                    </td>
                    <td class="text-xs">
                      <div><strong>{{ item.creator?.name || 'System' }}</strong></div>
                      <div class="text-muted">{{ formatDate(item.created_at) }}</div>
                    </td>
                    <td class="text-xs">
                      <div v-if="item.updater" class="text-info font-weight-bold">{{ item.updater?.name }}</div>
                      <div class="text-muted" v-if="item.updater">{{ formatDate(item.updated_at) }}</div>
                      <span v-else class="text-muted">N/A</span>
                    </td>
                    <td class="text-xs">
                      <div v-if="item.stopper" class="text-danger font-weight-bold">{{ item.stopper?.name }}</div>
                      <div class="text-muted" v-if="item.stopped_at">{{ formatDate(item.stopped_at) }}</div>
                      <span v-else class="text-muted">N/A</span>
                    </td>
                    <td class="text-xs">
                      <div class="text-danger font-weight-bold">
                        {{ item.deleter?.name || item.stopper?.name || item.updater?.name || item.creator?.name || 'System' }}
                      </div>
                      <div class="text-muted">{{ formatDate(item.deleted_at || item.updated_at) }}</div>
                    </td>
                    <td class="align-middle text-center">
                      <button 
                        class="btn btn-sm btn-success me-2 mb-0" 
                        @click="restorePlaylist(item.id)"
                        title="Restore record back to active Playlists table"
                      >
                        <i class="fas fa-undo me-1"></i> Restore
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-danger mb-0" 
                        @click="permanentlyDelete(item.id)"
                        title="Permanently remove from database"
                      >
                        <i class="fas fa-trash-alt me-1"></i> Permanent Delete
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="modal-footer bg-white border-top py-2 px-4 justify-content-end">
            <button type="button" class="btn btn-secondary mb-0" @click="showTrashModal = false">CLOSE</button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL 1: LIVE VIDEO STREAM PLAYER OVERLAY -->
    <div v-if="showVideoModal" class="modal-overlay p-2 p-md-0">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg overflow-hidden bg-black">
          <div class="modal-header bg-white d-flex justify-content-between align-items-center py-3 px-4 border-bottom">
            <div class="d-flex align-items-center">
              <span class="text-danger me-2 pulse-dot">🔴</span>
              <h5 class="mb-0 font-weight-bold text-dark me-3">{{ activeVideoTitle }}</h5>
              <span v-if="playlistQueue.length > 0" class="badge bg-gradient-info text-xs me-2">
                Item {{ currentPlaylistIndex + 1 }} of {{ playlistQueue.length }}
              </span>
            </div>

            <div class="d-flex align-items-center gap-2">
              <button 
                v-if="playlistQueue.length > 0"
                class="btn btn-sm mb-0 px-3 font-weight-bold" 
                :class="showQueueDrawer ? 'btn-primary' : 'btn-outline-primary'"
                @click="showQueueDrawer = !showQueueDrawer"
              >
                <i class="fas fa-list me-1"></i> QUEUE ({{ playlistQueue.length }})
              </button>

              <button 
                v-if="playlistQueue.length > 1" 
                class="btn btn-sm btn-outline-secondary mb-0" 
                @click="prevPlaylistItem"
                :disabled="currentPlaylistIndex === 0"
              >
                <i class="fas fa-step-backward"></i> Prev
              </button>

              <button 
                v-if="playlistQueue.length > 1" 
                class="btn btn-sm btn-outline-secondary mb-0" 
                @click="nextPlaylistItem"
                :disabled="currentPlaylistIndex === playlistQueue.length - 1"
              >
                Next <i class="fas fa-step-forward"></i>
              </button>

              <button class="btn btn-sm btn-outline-danger mb-0 px-3 font-weight-bold" @click="closeVideoModal">
                ✕ CLOSE
              </button>
            </div>
          </div>

          <div v-if="showQueueDrawer && playlistQueue.length > 0" class="bg-gradient-dark p-3 border-bottom">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="text-white mb-0 text-xs font-weight-bold"><i class="fas fa-list-ul me-1"></i> CURRENT PLAYLIST QUEUE</h6>
              <small class="text-white-50 text-xxs">Click any video to play</small>
            </div>
            <div class="d-flex gap-3 overflow-auto py-2">
              <div 
                v-for="(item, idx) in playlistQueue" 
                :key="item.id"
                class="card bg-dark text-white p-2 cursor-pointer border flex-shrink-0"
                :class="idx === currentPlaylistIndex ? 'border-success shadow-lg' : 'border-secondary opacity-7'"
                style="width: 170px;"
                @click="playPlaylistVideo(idx)"
              >
                <div class="position-relative mb-2" style="height: 75px;">
                  <img 
                    v-if="item.video_type === 'youtube' || !item.video_type"
                    :src="getYouTubeThumbnail(item.youtube_url) || defaultThumbnail" 
                    class="w-100 h-100 object-fit-cover border-radius-sm"
                    @error="onImgError"
                  />
                  <div v-else class="w-100 h-100 bg-secondary d-flex align-items-center justify-content-center border-radius-sm">
                    <i class="fas fa-file-video text-lg"></i>
                  </div>
                  <span v-if="idx === currentPlaylistIndex" class="badge bg-success position-absolute top-1 start-1 text-xxs">
                    NOW PLAYING
                  </span>
                </div>
                <p class="text-xs mb-0 text-truncate text-white font-weight-bold" :title="item.title">{{ item.title }}</p>
              </div>
            </div>
          </div>

          <div class="modal-body p-0 bg-black overflow-hidden d-flex justify-content-center align-items-center" style="min-height: 450px;">
            <video
              v-if="activeVideoType === 'upload'"
              :src="activeEmbedUrl"
              controls
              autoplay
              @ended="onVideoEnded"
              class="w-100 h-auto"
              style="max-height: 70vh; object-fit: contain; display: block;"
            ></video>

            <div v-else-if="activeEmbedUrl" class="ratio ratio-16x9 w-100">
              <iframe
                :src="activeEmbedUrl"
                title="Live Stream Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                style="border: 0; width: 100%; height: 100%;"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL 2: SELECTED PREVIEW MODAL -->
    <div v-if="showSelectedModal" class="modal-overlay p-2 p-md-0">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg w-100">
          <div class="modal-header border-0 py-3 px-4 bg-gradient-dark text-white">
            <h5 class="modal-title font-weight-bold text-white mb-0">
              <i class="fas fa-list-check me-2"></i> Selected Playlists ({{ selectedPlaylistsItems.length }})
            </h5>
            <button type="button" class="btn-close text-white" @click="showSelectedModal = false"></button>
          </div>
          <div class="modal-body p-4 bg-light" style="max-height: 60vh; overflow-y: auto;">
            <div v-if="selectedPlaylistsItems.length === 0" class="text-center py-4 text-secondary">
              <i class="fas fa-video-slash fa-3x mb-3 text-secondary"></i>
              <p class="mb-0 font-weight-bold">No playlists selected yet. Select checkboxes in the table below.</p>
            </div>
            <div class="row g-3" v-else>
              <div v-for="pl in selectedPlaylistsItems" :key="pl.id" class="col-12 col-md-6">
                <div class="card border-0 shadow-sm h-100 p-2 d-flex flex-row align-items-center justify-content-between">
                  <div class="d-flex align-items-center gap-3">
                    <img 
                      :src="getPlaylistThumbnail(pl)"
                      class="border-radius-sm object-fit-cover"
                      style="width: 70px; height: 50px;"
                      @error="onImgError"
                    />
                    <div>
                      <h6 class="text-sm font-weight-bold mb-0 text-truncate" style="max-width: 180px;">{{ pl.playlist_name || pl.title }}</h6>
                      <span class="badge text-xxs" :class="isVideoActive(pl) ? 'bg-success' : 'bg-secondary'">
                        {{ isVideoActive(pl) ? 'Active' : 'Inactive' }}
                      </span>
                    </div>
                  </div>
                  <button class="btn btn-link text-danger mb-0 p-2" @click="toggleVideoSelection(pl.id)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-white border-top py-2 px-4 justify-content-between">
            <button type="button" class="btn btn-secondary mb-0" @click="showSelectedModal = false">CLOSE</button>
            <button 
              type="button" 
              class="btn bg-gradient-success mb-0 font-weight-bold text-white" 
              @click="triggerGlobalEmergency"
            >
              <i class="fas fa-play me-2"></i> START BROADCAST
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL 3: GALLERY MODAL -->
    <div v-if="showGalleryModal" class="modal-overlay p-2 p-md-0">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg w-100">
          <div class="modal-header bg-gradient-dark text-white py-3 px-4 d-flex justify-content-between align-items-center">
            <h5 class="modal-title text-white font-weight-bold mb-0">
              <i class="fas fa-images me-2"></i> Media Gallery
            </h5>
            <div class="d-flex align-items-center gap-2">
              <button class="btn btn-xs btn-light mb-0" @click="openModal(false)">
                <i class="fas fa-plus me-1"></i> Add Video to Gallery
              </button>
              <button type="button" class="btn-close text-white ms-2" @click="closeGalleryModal"></button>
            </div>
          </div>

          <div class="modal-body p-4 bg-light" style="max-height: 70vh; overflow-y: auto;">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="d-flex align-items-center gap-3">
                <h6 class="font-weight-bold text-dark mb-0"><i class="fas fa-video me-1"></i> All YouTube Videos & Media</h6>
                <span class="badge bg-gradient-primary px-3 py-1 text-xs">
                  {{ selectedVideoIds.length }} Selected
                </span>
                <small class="text-muted text-xxs ms-2">(Max 1 YouTube Video allowed per playlist)</small>
              </div>
              <div>
                <button class="btn btn-xs btn-outline-secondary mb-0 me-1" @click="selectAllVideos">Select All</button>
                <button class="btn btn-xs btn-outline-secondary mb-0" @click="clearVideoSelection">Clear Selection</button>
              </div>
            </div>

            <div v-if="singleVideos.length === 0" class="text-center py-5 text-secondary">
              <i class="fas fa-film fa-3x mb-3 text-secondary"></i>
              <p class="font-weight-bold">No videos found in Gallery. Click "+ Add Video to Gallery" to upload files or paste YouTube links.</p>
            </div>

            <div class="row" v-else>
              <div v-for="video in singleVideos" :key="video.id" class="col-lg-3 col-md-4 col-sm-6 mb-4">
                <div
                  class="card h-100 gallery-card border-0 shadow-sm position-relative overflow-hidden cursor-pointer"
                  :class="{ 'selected-card': isVideoSelected(video.id) }"
                  @click="toggleVideoSelection(video.id)"
                >
                  <div v-if="isVideoSelected(video.id)" class="selection-badge">
                    <i class="fas fa-check-circle text-success"></i>
                  </div>

                  <span class="badge position-absolute top-2 start-2 z-index-2 text-xxs" :class="video.video_type === 'upload' ? 'bg-gradient-info' : 'bg-gradient-danger'">
                    {{ video.video_type === 'upload' ? 'FILE UPLOAD' : 'YOUTUBE' }}
                  </span>

                  <div class="gallery-thumb bg-dark position-relative">
                    <img
                      v-if="video.video_type === 'youtube' || !video.video_type"
                      :src="getYouTubeThumbnail(video.youtube_url) || defaultThumbnail"
                      alt="video thumbnail"
                      class="w-100 h-100 object-fit-cover"
                      @error="onImgError"
                    />
                    <div v-else class="w-100 h-100 d-flex align-items-center justify-content-center text-white bg-gradient-dark">
                      <i class="fas fa-file-video text-2xl"></i>
                    </div>
                  </div>

                  <div class="card-body p-3 text-center d-flex flex-column justify-content-between">
                    <h6 class="text-sm font-weight-bold mb-2 text-truncate" :title="video.title">{{ video.title }}</h6>
                    <div class="d-flex gap-1">
                      <button 
                        type="button" 
                        class="btn btn-xs w-100 mb-0 font-weight-bold" 
                        :class="isVideoSelected(video.id) ? 'btn-success' : 'btn-outline-primary'"
                        @click.stop="toggleVideoSelection(video.id)"
                      >
                        {{ isVideoSelected(video.id) ? 'SELECTED' : 'SELECT' }}
                      </button>
                      <button type="button" class="btn btn-xs btn-outline-danger mb-0" @click.stop="deleteVideo(video.id)">
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer bg-white border-top py-2 px-4 justify-content-between">
            <button type="button" class="btn btn-secondary mb-0" @click="closeGalleryModal">CANCEL</button>
            <button
              type="button"
              class="btn btn-primary mb-0 font-weight-bold"
              :disabled="selectedVideoIds.length === 0"
              @click="openPlaylistDetailsModal"
            >
              <i :class="playlistForm.id ? 'fas fa-edit me-1' : 'fas fa-plus-circle me-1'"></i>
              {{ playlistForm.id ? 'UPDATE PLAYLIST' : `CREATE PLAYLIST (${selectedVideoIds.length} SELECTED)` }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL 4: CONFIGURE PLAYLIST DETAILS -->
    <div v-if="showPlaylistDetailsModal" class="modal-overlay p-2 p-md-0">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 580px;">
        <div class="modal-content border-0 shadow-lg">
          <div class="modal-header bg-gradient-primary text-white py-2 px-3">
            <h5 class="modal-title text-white font-weight-bold mb-0">
              <i class="fas fa-list-check me-2"></i> {{ playlistForm.id ? 'Configure Playlist Details (Update)' : 'Configure Playlist Details' }}
            </h5>
            <button type="button" class="btn-close text-white" @click="showPlaylistDetailsModal = false"></button>
          </div>
          <div class="modal-body py-3 px-4">
            <form @submit.prevent="savePlaylistAndPlay">
              <div class="alert alert-info text-white text-xs py-2 px-3 mb-3 d-flex justify-content-between align-items-center">
                <span><i class="fas fa-info-circle me-1"></i> Total Videos Selected:</span>
                <span class="badge bg-white text-dark font-weight-bold">{{ selectedVideoIds.length }} Videos</span>
              </div>

              <div class="mb-3">
                <label class="form-label font-weight-bold text-dark mb-1">Playlist Name <span class="text-danger">*</span></label>
                <input
                  type="text"
                  v-model="playlistForm.name"
                  class="form-control px-2 border"
                  placeholder="e.g. Daily YouTube Broadcast Playlist"
                  required
                />
              </div>

              <!-- Multi Branch Selection Box -->
              <div class="mb-3">
                <label class="form-label font-weight-bold text-dark mb-1">Select Branch(es) <span class="text-danger">*</span></label>
                <div class="border rounded p-2 bg-white" style="max-height: 120px; overflow-y: auto;">
                  <div class="form-check mb-1">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="pl_branch_all" 
                      value="all" 
                      v-model="playlistForm.selectedBranches"
                      @change="handleBranchAllToggle('playlistForm')"
                    />
                    <label class="form-check-label font-weight-bold text-primary" for="pl_branch_all">
                      🌐 All Branches
                    </label>
                  </div>
                  <div v-for="branch in branches" :key="branch.id || branch.branch_id" class="form-check mb-1">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :id="'pl_branch_' + (branch.id || branch.branch_id)" 
                      :value="String(branch.id || branch.branch_id)" 
                      v-model="playlistForm.selectedBranches"
                      @change="handleSingleBranchToggle('playlistForm')"
                    />
                    <label class="form-check-label text-dark" :for="'pl_branch_' + (branch.id || branch.branch_id)">
                      {{ branch.branch_name || branch.name || branch.title || ('Branch #' + (branch.id || branch.branch_id)) }}
                    </label>
                  </div>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label font-weight-bold text-dark mb-1">Active From (Date & Time)</label>
                  <input
                    type="datetime-local"
                    v-model="playlistForm.active_from"
                    class="form-control px-2 border"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label font-weight-bold text-dark mb-1">Active Till (Date & Time)</label>
                  <input
                    type="datetime-local"
                    v-model="playlistForm.active_till"
                    class="form-control px-2 border"
                  />
                </div>
              </div>

              <div class="form-check form-switch ps-0 mb-3">
                <input
                  class="form-check-input ms-auto"
                  type="checkbox"
                  id="playlistStatusSwitch"
                  v-model="playlistForm.status"
                />
                <label class="form-check-label ms-3 font-weight-bold text-dark" for="playlistStatusSwitch">
                  Active Playlist
                </label>
              </div>

              <div class="modal-footer px-0 pb-0 pt-2 border-top">
                <button type="button" class="btn btn-secondary mb-0" @click="showPlaylistDetailsModal = false">Cancel</button>
                <button type="submit" class="btn btn-primary mb-0" :disabled="savingPlaylist">
                  <span v-if="savingPlaylist" class="spinner-border spinner-border-sm me-1"></span>
                  <i :class="playlistForm.id ? 'fas fa-save me-1' : 'fas fa-play me-1'"></i>
                  {{ playlistForm.id ? 'Update Playlist' : 'Save Playlist' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL 5: ADD NEW VIDEO TO GALLERY -->
    <div v-if="showModal" class="modal-overlay p-2 p-md-0">
      <div class="modal-dialog modal-dialog-centered modal-lg" style="max-width: 650px;">
        <div class="modal-content">
          <div class="modal-header bg-gradient-primary text-white py-2 px-3">
            <h5 class="modal-title text-white">Add Video to Gallery</h5>
            <button type="button" class="btn-close text-white" @click="closeModal"></button>
          </div>
          <div class="modal-body py-3 px-4">
            <form @submit.prevent="saveVideo">
              <div class="mb-3">
                <label class="form-label font-weight-bold text-dark d-block mb-1">Video Source Type <span class="text-danger">*</span></label>
                <div class="btn-group w-100" role="group">
                  <button type="button" class="btn btn-sm mb-0" :class="form.video_type === 'youtube' ? 'btn-primary' : 'btn-outline-primary'" @click="form.video_type = 'youtube'"><i class="fab fa-youtube me-1"></i> YouTube URL</button>
                  <button type="button" class="btn btn-sm mb-0" :class="form.video_type === 'upload' ? 'btn-primary' : 'btn-outline-primary'" @click="form.video_type = 'upload'"><i class="fas fa-upload me-1"></i> Upload Video File</button>
                </div>
              </div>

              <div class="mb-2">
                <label class="form-label font-weight-bold text-dark mb-1">Title <span class="text-danger">*</span></label>
                <input type="text" v-model="form.title" class="form-control px-2 border" placeholder="e.g. YouTube Live Stream 1" required />
              </div>

              <div v-if="form.video_type === 'youtube'" class="mb-2">
                <label class="form-label font-weight-bold text-dark mb-1">YouTube URL <span class="text-danger">*</span></label>
                <input 
                  type="url"
                  v-model="form.youtube_url" 
                  class="form-control px-2 border" 
                  placeholder="https://www.youtube.com/watch?v=..." 
                  :required="form.video_type === 'youtube'"
                />
              </div>

              <div v-if="form.video_type === 'upload'" class="mb-2">
                <label class="form-label font-weight-bold text-dark mb-1">Choose Video File <span class="text-danger">*</span></label>
                <input type="file" accept="video/*" @change="handleFileUpload" class="form-control px-2 border" :required="form.video_type === 'upload' && !isEdit" />
              </div>

              <div class="form-check form-switch ps-0 mb-2">
                <input class="form-check-input ms-auto" type="checkbox" id="statusSwitch" v-model="form.status" />
                <label class="form-check-label ms-3 font-weight-bold text-dark" for="statusSwitch">Active Video</label>
              </div>

              <div class="modal-footer px-0 pb-0 pt-2">
                <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                  Add to Gallery
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { io } from "socket.io-client";

export default {
  name: "LiveVideos",
  data() {
    return {
      socket: null,
      videos: [],
      trashedPlaylists: [],
      branches: [],
      loading: false,
      saving: false,
      savingPlaylist: false,
      searchQuery: "",
      showModal: false,
      isEdit: false,
      showTrashModal: false,
      showRecordHistoryModal: false,
      activeHistoryRecord: null,
      activeHistoryLogs: [],
      showVideoModal: false,
      activeVideoTitle: "",
      activeEmbedUrl: "",
      activeVideoType: "youtube",
      showGalleryModal: false,
      showSelectedModal: false,
      selectedVideoIds: [],
      playlistQueue: [],
      currentPlaylistIndex: 0,
      showQueueDrawer: true,
      showPlaylistDetailsModal: false,
      playlistForm: {
        id: null,
        name: "",
        selectedBranches: ["all"],
        active_from: "",
        active_till: "",
        status: true,
      },
      selectedFile: null,
      form: {
        id: null,
        selectedBranches: ["all"],
        title: "",
        video_type: "youtube",
        youtube_url: "",
        video_file: "",
        status: true,
        active_from: "",
        active_till: "",
      },
      defaultThumbnail: "https://via.placeholder.com/150?text=Playlist",
    };
  },
  computed: {
    singleVideos() {
      return this.videos.filter((v) => v.video_type !== "playlist");
    },
    savedPlaylists() {
      return this.videos.filter((v) => v.video_type === "playlist");
    },
    filteredTablePlaylists() {
      if (!this.searchQuery) return this.savedPlaylists;
      const q = this.searchQuery.toLowerCase();
      return this.savedPlaylists.filter(
        (v) =>
          (v.title && v.title.toLowerCase().includes(q)) ||
          (v.playlist_name && v.playlist_name.toLowerCase().includes(q)) ||
          (v.branch_name && v.branch_name.toLowerCase().includes(q))
      );
    },
    selectedPlaylistsItems() {
      return this.savedPlaylists.filter((v) => this.isVideoSelected(v.id));
    },
    selectedVideosCount() {
      return this.selectedVideoIds.length;
    },
    activeVideosCount() {
      return this.savedPlaylists.filter((v) => this.isVideoActive(v)).length;
    },
    isGlobalLive() {
      return this.videos.some((v) => v.is_live == 1 || v.is_live === true || v.is_live === "1");
    },
  },
  methods: {
    initSocket() {
      const token = localStorage.getItem("token");
      let serverUrl = process.env.VUE_APP_API_BASE_URL || "http://localhost:3005";
      try {
        const urlHelper = new URL(serverUrl);
        serverUrl = urlHelper.origin;
      } catch (e) {
        console.error("Invalid URL format in Socket Init", e);
      }
      this.socket = io(serverUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        forceNew: true
      });
      this.socket.on("connect_error", (err) => {
        console.error("Socket Connection Error:", err);
        this.savingPlaylist = false; 
      });
      this.socket.on("playlist_updated_broadcast", () => {
        this.fetchVideos();
      });
    },

    getAuthHeaders() {
      const token = localStorage.getItem("token");
      return { Authorization: `Bearer ${token}` };
    },

    openHistoryModal(item) {
      this.activeHistoryRecord = item;
      this.activeHistoryLogs = this.ensureArray(item.update_history).slice(0, 5);
      this.showRecordHistoryModal = true;
    },

    getActionIcon(action) {
      if (action === 'CREATED') return 'fa-plus text-success';
      if (action === 'UPDATED') return 'fa-edit text-info';
      if (action === 'STOPPED') return 'fa-stop-circle text-danger';
      if (action === 'TRIGGERED_LIVE') return 'fa-bolt text-warning';
      if (action === 'RESTORED') return 'fa-undo text-primary';
      return 'fa-history text-secondary';
    },

    formatValue(val) {
      if (val === null || val === undefined) return 'None';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    },

    ensureArray(data) {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      try { return JSON.parse(data) || []; } catch (e) { return []; }
    },

    openTrashModal() {
      this.fetchTrashVideos();
      this.showTrashModal = true;
    },

    async fetchTrashVideos() {
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        const res = await axios.get(`${baseUrl}/v1/admin/live-video/trash-list?t=${Date.now()}`, {
          headers: { ...this.getAuthHeaders(), "Cache-Control": "no-cache" },
        });
        if (res.data.success) {
          this.trashedPlaylists = [...(res.data.data || [])];
        }
      } catch (error) {
        console.error("Trash fetch error:", error);
      }
    },

    async restorePlaylist(id) {
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        await axios.post(`${baseUrl}/v1/admin/live-video/restore`, { id }, { headers: this.getAuthHeaders() });
        Swal.fire({ icon: "success", title: "Restored", timer: 1800, showConfirmButton: false });
        await this.fetchVideos();
        await this.fetchTrashVideos();
      } catch (error) {
        Swal.fire("Error", "Failed to restore", "error");
      }
    },

    permanentlyDelete(id) {
      Swal.fire({
        title: "Permanently Delete?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const baseUrl = process.env.VUE_APP_API_BASE_URL;
            await axios.delete(`${baseUrl}/v1/admin/live-video/permanent-delete/${id}`, { headers: this.getAuthHeaders() });
            Swal.fire("Deleted!", "Permanently removed.", "success");
            await this.fetchTrashVideos();
          } catch (error) {
            Swal.fire("Error", "Failed to delete", "error");
          }
        }
      });
    },

    toggleVideoSelection(id) {
      const numId = Number(id);
      const targetVideo = this.videos.find((v) => Number(v.id) === numId);
      const isAlreadySelected = this.selectedVideoIds.some((v) => Number(v) === numId);
      if (isAlreadySelected) {
        this.selectedVideoIds = this.selectedVideoIds.filter((v) => Number(v) !== numId);
      } else {
        if (targetVideo && (targetVideo.video_type === "youtube" || !targetVideo.video_type)) {
          const youtubeVideoIds = this.singleVideos.filter((v) => v.video_type === "youtube" || !v.video_type).map((v) => Number(v.id));
          this.selectedVideoIds = this.selectedVideoIds.filter((v) => !youtubeVideoIds.includes(Number(v)));
        }
        this.selectedVideoIds.push(numId);
      }
    },

    isVideoSelected(id) { return this.selectedVideoIds.some((v) => Number(v) === Number(id)); },

    selectAllVideos() {
      const uploadIds = this.singleVideos.filter((v) => v.video_type === "upload").map((v) => Number(v.id));
      const firstYoutube = this.singleVideos.find((v) => v.video_type === "youtube" || !v.video_type);
      this.selectedVideoIds = [...uploadIds];
      if (firstYoutube) this.selectedVideoIds.push(Number(firstYoutube.id));
    },

    clearVideoSelection() {
      this.selectedVideoIds = [];
      this.resetPlaylistForm();
    },

    handleBranchAllToggle(formKey) {
      const isAllChecked = this[formKey].selectedBranches.includes("all");
      const allBranchIds = this.branches.map((b) => String(b.id || b.branch_id));
      this[formKey].selectedBranches = isAllChecked ? ["all", ...allBranchIds] : [];
    },

    handleSingleBranchToggle(formKey) {
      const allBranchIds = this.branches.map((b) => String(b.id || b.branch_id));
      const selectedIndividual = this[formKey].selectedBranches.filter((id) => id !== "all");
      if (selectedIndividual.length === allBranchIds.length && allBranchIds.length > 0) {
        if (!this[formKey].selectedBranches.includes("all")) this[formKey].selectedBranches.push("all");
      } else {
        const index = this[formKey].selectedBranches.indexOf("all");
        if (index > -1) this[formKey].selectedBranches.splice(index, 1);
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) this.selectedFile = file;
    },

    formatDateForInput(dateString) {
      if (!dateString) return "";
      let formattedStr = String(dateString).trim().replace(" ", "T");
      const date = new Date(formattedStr);
      if (isNaN(date.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(String(dateString).trim().replace(" ", "T"));
      return isNaN(date.getTime()) ? dateString : date.toLocaleString("en-US", { month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
    },

    isVideoActive(video) {
      if (!video || (video.status != 1 && video.status !== true)) return false;
      const now = new Date();
      if (video.active_from && now < new Date(video.active_from).setHours(0,0,0,0)) return false;
      if (video.active_till && now > new Date(video.active_till).setHours(23,59,59,999)) return false;
      return true;
    },

    isVideoLive(video) { return video && (video.is_live == 1 || video.is_live === true); },

    getYouTubeNoCookieEmbedUrl(url) {
      const match = url?.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
      return (match && match[2].length === 11) ? `https://www.youtube-nocookie.com/embed/${match[2]}?autoplay=1&rel=0&modestbranding=1&controls=1` : null;
    },

    getPlaylistVideoIds(videoFileStr) {
      try {
        const parsed = typeof videoFileStr === "string" ? JSON.parse(videoFileStr) : videoFileStr;
        return Array.isArray(parsed) ? parsed.map(Number) : [];
      } catch (e) {
        console.warn("JSON parse error for video IDs", e);
        return [];
      }
    },

    getPlaylistThumbnail(playlist) {
      const ids = this.getPlaylistVideoIds(playlist.playlist_videos || playlist.video_file);
      const first = this.singleVideos.find((v) => Number(v.id) === Number(ids[0]));
      return first?.youtube_url ? this.getYouTubeThumbnail(first.youtube_url) : this.defaultThumbnail;
    },

    async triggerGlobalEmergency() {
      const target = this.savedPlaylists.filter((v) => (this.selectedVideoIds.length === 0 || this.isVideoSelected(v.id)) && this.isVideoActive(v));
      if (!target.length) return Swal.fire("Notice", "No active playlists.", "info");
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        await axios.post(`${baseUrl}/v1/admin/live-video/trigger-global`, { ids: target.map((v) => Number(v.id)) }, { headers: this.getAuthHeaders() });
        await this.fetchVideos();
        this.showSelectedModal = false;
        Swal.fire({ icon: "success", title: "Broadcast Live", timer: 2000, showConfirmButton: false });
      } catch (e) {
        console.error("Global trigger error", e);
        Swal.fire("Error", "Failed", "error");
      }
    },

    async triggerPlaylistBroadcast(playlist) {
      if (!this.isVideoActive(playlist)) return Swal.fire("Warning", "Playlist inactive.", "warning");
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        await axios.post(`${baseUrl}/v1/admin/live-video/trigger`, { id: playlist.id }, { headers: this.getAuthHeaders() });
        if (!this.selectedVideoIds.includes(Number(playlist.id))) this.selectedVideoIds.push(Number(playlist.id));
        await this.fetchVideos();
        Swal.fire({ icon: "success", title: "Started", timer: 1800, showConfirmButton: false });
      } catch (e) {
        console.error("Playlist trigger error", e);
        Swal.fire("Error", "Failed", "error");
      }
    },

    stopSingleEmergency(video) {
      Swal.fire({ title: "Stop?", icon: "warning", showCancelButton: true }).then(async (res) => {
        if (res.isConfirmed) {
          try {
            const baseUrl = process.env.VUE_APP_API_BASE_URL;
            await axios.post(`${baseUrl}/v1/admin/live-video/stop`, { id: video.id }, { headers: this.getAuthHeaders() });
            await this.fetchVideos();
            await this.fetchTrashVideos();
            this.selectedVideoIds = this.selectedVideoIds.filter(id => Number(id) !== Number(video.id));
            Swal.fire({ icon: "success", title: "Stopped", timer: 1800, showConfirmButton: false });
          } catch (e) {
            console.error("Stop single error", e);
            Swal.fire("Error", "Failed", "error");
          }
        }
      });
    },

    stopAllBroadcast() {
      Swal.fire({ title: "Stop All?", icon: "warning", showCancelButton: true }).then(async (res) => {
        if (res.isConfirmed) {
          try {
            const baseUrl = process.env.VUE_APP_API_BASE_URL;
            await axios.post(`${baseUrl}/v1/admin/live-video/stop`, {}, { headers: this.getAuthHeaders() });
            await this.fetchVideos();
            await this.fetchTrashVideos();
            this.selectedVideoIds = [];
            this.closeVideoModal();
            Swal.fire({ icon: "success", title: "All Stopped", timer: 2000, showConfirmButton: false });
          } catch (e) {
            console.error("Stop all error", e);
            Swal.fire("Error", "Failed", "error");
          }
        }
      });
    },

    resetPlaylistForm() {
      const allBranchIds = this.branches.map((b) => String(b.id || b.branch_id));
      this.playlistForm = { id: null, name: "", selectedBranches: ["all", ...allBranchIds], active_from: "", active_till: "", status: true };
    },

    openGalleryModal() { this.showGalleryModal = true; },
    closeGalleryModal() { this.showGalleryModal = false; },
    openPlaylistDetailsModal() {
      if (!this.selectedVideoIds.length) return Swal.fire("Warning", "Select a video.", "warning");
      this.showPlaylistDetailsModal = true;
    },

    editPlaylist(playlist) {
      this.loadPlaylist(playlist);
      this.showGalleryModal = true;
    },

    loadPlaylist(pl) {
      this.playlistForm.id = pl.id;
      this.selectedVideoIds = [...this.getPlaylistVideoIds(pl.playlist_videos || pl.video_file)];
      this.playlistForm.name = pl.playlist_name || pl.title;
      const allBranchIds = this.branches.map((b) => String(b.id || b.branch_id));
      if (pl.branch_id === "all" || !pl.branch_id) {
        this.playlistForm.selectedBranches = ["all", ...allBranchIds];
      } else {
        const ids = String(pl.branch_id).split(",");
        this.playlistForm.selectedBranches = ids;
        if (ids.length === allBranchIds.length) this.playlistForm.selectedBranches.push("all");
      }
      this.playlistForm.active_from = this.formatDateForInput(pl.active_from);
      this.playlistForm.active_till = this.formatDateForInput(pl.active_till);
      this.playlistForm.status = pl.status == 1 || pl.status === true;
    },

    // REPLACED SOCKET WITH STANDARD API (PUT/POST)
    async savePlaylistAndPlay() {
      if (!this.playlistForm.name.trim()) return Swal.fire("Warning", "Name required.", "warning");
      this.savingPlaylist = true;
      const branchPayload = this.playlistForm.selectedBranches.includes("all") ? "all" : this.playlistForm.selectedBranches.filter(id => id !== "all").join(",");
      const payload = {
        branch_id: branchPayload,
        title: this.playlistForm.name,
        video_type: "playlist",
        playlist_name: this.playlistForm.name,
        playlist_videos: this.selectedVideoIds.map(Number),
        status: this.playlistForm.status ? 1 : 0,
        active_from: this.playlistForm.active_from || null,
        active_till: this.playlistForm.active_till || null,
      };

      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        const headers = { ...this.getAuthHeaders() };
        let response;
        if (this.playlistForm.id) {
          // Standard update call using PUT
          response = await axios.put(`${baseUrl}/v1/admin/live-video/update/${this.playlistForm.id}`, payload, { headers });
        } else {
          // Standard creation call using POST
          response = await axios.post(`${baseUrl}/v1/admin/live-video/add`, payload, { headers });
        }

        if (response.data.success) {
          Swal.fire({ icon: "success", title: "Saved", timer: 2000, showConfirmButton: false });
          this.showPlaylistDetailsModal = false;
          this.closeGalleryModal();
          this.clearVideoSelection();
          this.fetchVideos(); 
        }
      } catch (error) {
        console.error("Save playlist error", error);
        Swal.fire("Error", error.response?.data?.message || "Failed", "error");
      } finally { this.savingPlaylist = false; }
    },

    playPlaylistItemDirectly(playlist) {
      const ids = this.getPlaylistVideoIds(playlist.playlist_videos || playlist.video_file);
      const videos = this.singleVideos.filter((v) => ids.includes(Number(v.id)));
      if (videos.length > 0) {
        this.playlistQueue = videos;
        this.currentPlaylistIndex = 0;
        this.showQueueDrawer = true;
        this.playPlaylistVideo(0);
      }
    },

    playPlaylistVideo(index) {
      if (index >= 0 && index < this.playlistQueue.length) {
        this.currentPlaylistIndex = index;
        this.openVideoModal(this.playlistQueue[index]);
      }
    },

    nextPlaylistItem() { if (this.currentPlaylistIndex < this.playlistQueue.length - 1) this.playPlaylistVideo(this.currentPlaylistIndex + 1); },
    prevPlaylistItem() { if (this.currentPlaylistIndex > 0) this.playPlaylistVideo(this.currentPlaylistIndex - 1); },
    onVideoEnded() { if (this.currentPlaylistIndex < this.playlistQueue.length - 1) this.nextPlaylistItem(); },

    openVideoModal(video) {
      if (!this.isVideoActive(video)) return Swal.fire("Warning", "Video Inactive", "warning");
      this.activeVideoTitle = video.title || "Live Stream";
      this.activeVideoType = video.video_type || "youtube";
      if (video.video_type === "upload") {
        const apiBase = process.env.VUE_APP_API_BASE_URL || "";
        this.activeEmbedUrl = video.video_file.startsWith("http") ? video.video_file : `${apiBase.replace('/v1/admin', '')}${video.video_file}`;
        this.showVideoModal = true;
      } else {
        const url = this.getYouTubeNoCookieEmbedUrl(video.youtube_url);
        if (url) { this.activeEmbedUrl = url; this.showVideoModal = true; }
      }
    },

    closeVideoModal() { this.showVideoModal = false; this.activeEmbedUrl = ""; },

    async fetchVideos() {
      this.loading = true;
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        const res = await axios.get(`${baseUrl}/v1/admin/live-video/list?t=${Date.now()}`, { headers: this.getAuthHeaders() });
        if (res.data.success) {
          this.videos = res.data.data || [];
          this.videos.filter(v => v.is_live == 1).forEach(v => {
            if (!this.selectedVideoIds.includes(Number(v.id))) this.selectedVideoIds.push(Number(v.id));
          });
        }
      } catch (error) {
        console.error("Fetch videos error", error);
      } finally { this.loading = false; }
    },

    async fetchBranches() {
      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        const res = await axios.get(`${baseUrl}/v1/admin/branches`, { headers: this.getAuthHeaders() });
        this.branches = res.data.data || res.data.rows || res.data || [];
      } catch (e) {
        console.error("Branch fetch error:", e);
      }
    },

    openModal(isEdit = false) {
      this.isEdit = isEdit;
      this.selectedFile = null;
      const allBranchIds = this.branches.map((b) => String(b.id || b.branch_id));
      this.form = { id: null, selectedBranches: ["all", ...allBranchIds], title: "", video_type: "youtube", youtube_url: "", video_file: "", status: true, active_from: "", active_till: "" };
      this.showModal = true;
    },

    closeModal() { this.showModal = false; },

    async saveVideo() {
      this.saving = true;
      const branchPayload = this.form.selectedBranches.includes("all") ? "all" : this.form.selectedBranches.filter(id => id !== "all").join(",");
      const formData = new FormData();
      formData.append("title", this.form.title);
      formData.append("video_type", this.form.video_type);
      formData.append("branch_id", branchPayload);
      formData.append("status", this.form.status ? 1 : 0);
      if (this.form.active_from) formData.append("active_from", this.form.active_from);
      if (this.form.active_till) formData.append("active_till", this.form.active_till);
      if (this.form.video_type === "youtube") formData.append("youtube_url", this.form.youtube_url);
      else if (this.selectedFile) formData.append("video_file", this.selectedFile);

      try {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        await axios.post(`${baseUrl}/v1/admin/live-video/add`, formData, { headers: this.getAuthHeaders() });
        Swal.fire({ icon: "success", title: "Saved", timer: 2000, showConfirmButton: false });
        this.closeModal();
        await this.fetchVideos();
        this.openGalleryModal();
      } catch (e) { 
        console.error("Save video error", e);
        Swal.fire("Error", "Failed", "error"); 
      }
      finally { this.saving = false; }
    },

    deleteVideo(id) {
      Swal.fire({ title: "Trash Bin?", icon: "warning", showCancelButton: true }).then(async (res) => {
        if (res.isConfirmed) {
          try {
            const baseUrl = process.env.VUE_APP_API_BASE_URL;
            await axios.delete(`${baseUrl}/v1/admin/live-video/delete/${id}`, { headers: this.getAuthHeaders() });
            Swal.fire("Deleted", "Moved to Trash.", "success");
            await this.fetchVideos();
            await this.fetchTrashVideos();
          } catch (e) {
            console.error("Delete video error:", e);
          }
        }
      });
    },

    getYouTubeThumbnail(url) {
      const match = url?.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
      return (match && match[2].length === 11) ? `https://img.youtube.com/vi/${match[2]}/hqdefault.jpg` : null;
    },
    onImgError(event) { event.target.src = this.defaultThumbnail; },
  },
  mounted() {
    this.initSocket();
    this.fetchVideos();
    this.fetchBranches();
    this.fetchTrashVideos();
  },
};
</script>

<style scoped>
.timeline-block { display: flex; align-items: flex-start; }
.timeline-step { width: 32px; height: 32px; border-radius: 50%; background: #f6f9fc; display: flex; align-items: center; justify-content: center; margin-right: 15px; flex-shrink: 0; }
.timeline-content { flex-grow: 1; }
.audit-trash-btn { border: 1.5px solid #e91e63 !important; color: #e91e63 !important; border-radius: 20px !important; background-color: transparent !important; padding: 0.375rem 1rem !important; }
.audit-trash-btn:hover { background-color: #e91e63 !important; color: #ffffff !important; }
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.6); display: flex; justify-content: center; align-items: center; z-index: 1060; }
.modal-dialog { width: 95%; max-width: 700px; }
.modal-xl { max-width: 1140px; }
.bg-light-danger { background-color: rgba(255, 0, 0, 0.05) !important; }
.pulse-red-small { animation: pulse-animation 1.5s infinite; }
@keyframes pulse-animation { 0% { box-shadow: 0 0 0 0px rgba(255, 82, 82, 0.7); } 100% { box-shadow: 0 0 0 8px rgba(255, 82, 82, 0); } }
.cursor-pointer { cursor: pointer; }
.object-fit-cover { object-fit: cover; }
.bg-black { background-color: #000000 !important; }
.pulse-dot { font-size: 14px; animation: pulse 1.5s infinite; }
@keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }
.gallery-card { transition: all 0.2s ease-in-out; border: 2px solid transparent !important; }
.gallery-card:hover { transform: translateY(-3px); }
.selected-card { border: 2px solid #2dce89 !important; box-shadow: 0 4px 12px rgba(45, 206, 137, 0.3) !important; }
.selection-badge { position: absolute; top: 8px; right: 8px; z-index: 10; font-size: 20px; background: #ffffff; border-radius: 50%; line-height: 1; }
.gallery-thumb { height: 120px; }
</style>